import { PageShell } from '../../layout/Shell';
import { Icon } from '../../shared/design/Icons';
import { Button } from '../../shared/design/Primitives';

export function AboutPage() {
  return (
    <PageShell active="about">
      <div className="tm" style={{ maxWidth: 880, margin: '0 auto', padding: '60px 24px 80px' }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          About
        </div>
        <h1 className="h1" style={{ marginBottom: 20 }}>
          What this tool does — and what it doesn't.
        </h1>
        <p className="p" style={{ fontSize: 16, color: 'var(--fg-2)', marginBottom: 32 }}>
          Trackmania records every input the car receives at 50 Hz inside the .Replay.gbx file. This site reads those inputs back
          out and tells you whether they look like an analog stick or like a keyboard pretending to be one.
        </p>

        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-8)',
            padding: 24,
            marginBottom: 40,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 18 }}>
            Analog vs. action-key — visual difference
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ok)', marginBottom: 8 }}>Analog (stick / wheel)</div>
              <svg viewBox="0 0 200 80" style={{ width: '100%', height: 90 }}>
                <line x1="0" y1="40" x2="200" y2="40" stroke="var(--fg-dim)" strokeDasharray="2 3" strokeWidth="0.5" />
                <path
                  d={(() => {
                    let p = '';
                    for (let i = 0; i <= 200; i += 2) {
                      const v = 40 + 22 * Math.sin(i * 0.05) + 6 * Math.sin(i * 0.12);
                      p += i === 0 ? `M${i} ${v.toFixed(1)}` : ` L${i} ${v.toFixed(1)}`;
                    }
                    return p;
                  })()}
                  fill="none"
                  stroke="var(--ok)"
                  strokeWidth="1.5"
                />
              </svg>
              <p className="small" style={{ marginTop: 8 }}>
                Continuous curve — every sample differs from the next by a fraction.
              </p>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bad)', marginBottom: 8 }}>Action-key (snapped)</div>
              <svg viewBox="0 0 200 80" style={{ width: '100%', height: 90 }}>
                <line x1="0" y1="40" x2="200" y2="40" stroke="var(--fg-dim)" strokeDasharray="2 3" strokeWidth="0.5" />
                <path
                  d={(() => {
                    const levels = [60, 40, 20];
                    const segs = [0, 40, 60, 100, 130, 170, 200];
                    const vals = [60, 20, 60, 20, 60, 20];
                    let p = `M0 ${levels[0]}`;
                    for (let i = 0; i < vals.length; i++) {
                      p += ` L${segs[i + 1]} ${vals[i]}`;
                      if (i < vals.length - 1) p += ` L${segs[i + 1]} ${vals[i + 1] !== undefined ? vals[i + 1] : vals[i]}`;
                    }
                    return p;
                  })()}
                  fill="none"
                  stroke="var(--bad)"
                  strokeWidth="1.5"
                />
                <g fill="var(--bad)" opacity="0.5">
                  <circle cx="20" cy="60" r="1.5" />
                  <circle cx="40" cy="60" r="1.5" />
                  <circle cx="50" cy="20" r="1.5" />
                  <circle cx="80" cy="20" r="1.5" />
                  <circle cx="110" cy="60" r="1.5" />
                  <circle cx="140" cy="60" r="1.5" />
                </g>
              </svg>
              <p className="small" style={{ marginTop: 8 }}>
                Steps between a small set of exact values — the giveaway pattern.
              </p>
            </div>
          </div>
        </div>

        <h2 className="h2" style={{ marginBottom: 18 }}>
          FAQ
        </h2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            background: 'var(--border)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-8)',
            overflow: 'hidden',
          }}
        >
          {[
            {
              q: 'Does this prove cheating?',
              a: "No. It's a heuristic. The tier is our best guess based on input patterns. A low tier means the inputs look suspicious — investigation by humans is still needed.",
            },
            {
              q: 'What about controllers?',
              a: 'Good controllers register fine — most stick noise produces continuous values. Cheap controllers with dead-zone snapping may produce false positives at low tiers.',
            },
            {
              q: 'I play clean but show as C or D tier. Why?',
              a: 'Common causes: rebound bindings configured but not abused, very few inputs (short replays), or strong dead-zones in the in-game settings. The detail panel shows exactly which values triggered the verdict — you can tune the deadzone there.',
            },
            {
              q: 'Where does the input data come from?',
              a: 'Straight from the .Replay.gbx file Nadeo writes after every run. We never simulate the run — we read what the game recorded.',
            },
          ].map((f, i) => (
            <details key={i} style={{ background: 'var(--bg-surface)', padding: '14px 18px' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{f.q}</span>
                <Icon.Chevron style={{ color: 'var(--fg-3)' }} />
              </summary>
              <p className="p" style={{ marginTop: 10, color: 'var(--fg-2)' }}>
                {f.a}
              </p>
            </details>
          ))}
        </div>

        <h2 className="h2" style={{ marginTop: 48, marginBottom: 14 }}>
          Credits & links
        </h2>
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-8)',
            padding: 22,
          }}
        >
          <div className="p" style={{ marginBottom: 14, color: 'var(--fg-2)' }}>
            Built by <span style={{ color: 'var(--accent)' }}>Proxo</span> and{' '}
            <span style={{ color: 'var(--accent)' }}>SindreMA</span>. Replay format reference from Nadeo and the GBX docs project.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button variant="secondary" size="sm" icon={<Icon.Link />}>
              Source on GitHub
            </Button>
            <Button variant="secondary" size="sm" icon={<Icon.Link />}>
              GBX format docs
            </Button>
            <Button variant="ghost" size="sm" icon={<Icon.Warn />}>
              Report a bug
            </Button>
            <Button variant="ghost" size="sm" icon={<Icon.Plus />}>
              Suggest a feature
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
